import { Link } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { GlobalSchema } from "@/components/seo/GlobalSchema";
import { JsonLd } from "@/components/seo/JsonLd";
import { breadcrumbSchema } from "@/components/seo/schemas";
import { useSchemaIndexable } from "@/components/seo/useSchemaIndexable";

const FAQ_ITEMS = [
  {
    question: "¿Qué es Bandflow?",
    answer:
      "Bandflow es un directorio colaborativo de salas de conciertos que reúne información útil para facilitar la organización de actuaciones en directo. La plataforma permite consultar datos sobre recintos y, con el tiempo, incorporar valoraciones y experiencias compartidas por personas que ya han trabajado en ellos.",
  },
  {
    question: "¿A quién va dirigido Bandflow?",
    answer:
      "Bandflow está pensado para bandas, artistas, promotores y profesionales de la música en directo que necesitan encontrar salas y tomar mejores decisiones antes de organizar un concierto. También puede ser una herramienta útil para las propias salas, ya que les permite ganar visibilidad y entender mejor las necesidades de quienes programan eventos en ellas.",
  },
  {
    question: "¿Bandflow permite reservar salas directamente?",
    answer:
      "Bandflow nace como una plataforma para descubrir, comparar y evaluar salas. Su objetivo principal es centralizar información relevante para reducir la fricción en el proceso de búsqueda. Algunas funcionalidades podrán evolucionar con el tiempo, pero el foco actual está en facilitar la toma de decisiones con más contexto y menos incertidumbre.",
  },
  {
    question: "¿Qué tipo de información puedo encontrar sobre una sala?",
    answer:
      "La información puede incluir aspectos como el aforo, las condiciones técnicas, determinados servicios disponibles, la ubicación y otros datos que resultan relevantes para una banda o promotora a la hora de valorar si ese espacio encaja con su concierto.",
  },
  {
    question: "¿De dónde sale la información de las salas?",
    answer:
      "La información puede proceder de distintas fuentes públicas, de aportaciones de la comunidad y de actualizaciones facilitadas por las propias salas. El objetivo de Bandflow es construir una base de datos cada vez más útil, práctica y fiable para el sector.",
  },
  {
    question: "¿Las salas pueden corregir o actualizar su información?",
    answer:
      "Sí. Si una sala detecta que algún dato no es correcto, está incompleto o quiere ampliarlo, puede ponerse en contacto con Bandflow para solicitar una actualización. La intención es que la información sea lo más precisa y útil posible para todas las partes.",
  },
  {
    question: "¿Las salas pueden participar activamente en Bandflow?",
    answer:
      "Sí. Bandflow quiere contar también con la colaboración de las salas. La plataforma no busca señalar ni perjudicar a nadie, sino contribuir a un ecosistema más transparente, donde una mejor información ayude a generar más confianza y mejores relaciones entre salas, bandas y promotores.",
  },
  {
    question: "¿Las bandas pueden dejar opiniones o feedback sobre una sala?",
    answer:
      "La visión de Bandflow es dar espacio a la experiencia real de la comunidad musical. Ese feedback puede ser útil tanto para quienes estén buscando una sala como para los propios recintos, que pueden usarlo para detectar puntos de mejora y ofrecer un mejor servicio a sus clientes.",
  },
  {
    question: "¿El objetivo del feedback es perjudicar a las salas?",
    answer:
      "No. El propósito no es generar conflicto, sino favorecer una mejora continua en el sector. Una sala que conoce mejor cómo viven su experiencia las bandas y promotoras tiene más herramientas para reforzar aquello que funciona y ajustar aquello que puede mejorar.",
  },
  {
    question: "¿Bandflow verifica toda la información publicada?",
    answer:
      "Bandflow trabaja para que la información sea cada vez más rigurosa y útil, pero el proyecto está construido desde una lógica colaborativa y en evolución. Por eso, la plataforma irá mejorando sus procesos de revisión y actualización a medida que crezca la comunidad y aumente la participación de salas y usuarios.",
  },
  {
    question: "¿Bandflow es solo para grandes salas?",
    answer:
      "No. Bandflow quiere reflejar la diversidad real del circuito musical, desde salas consolidadas hasta espacios pequeños o emergentes que forman parte de la vida cultural de una ciudad.",
  },
  {
    question: "¿Cuánto cuesta usar Bandflow?",
    answer: "Bandflow es una herramienta gratuita.",
  },
  {
    question: "¿Cómo puedo contactar con Bandflow?",
    answer:
      "Si eres una banda, un promotor o una sala y quieres compartir información, corregir un dato o colaborar con el proyecto, puedes escribirnos a través del canal de contacto disponible en la plataforma.",
  },
];

export default function FAQ() {
  useSeoMeta({
    pageType: "static_page",
    entityId: "preguntas-frecuentes",
    variables: { titulo_pagina: "Preguntas frecuentes sobre Bandflow" },
    canonicalPath: "/preguntas-frecuentes/",
  });

  const isIndexable = useSchemaIndexable("static_page", "preguntas-frecuentes");

  const faqSchema = {
    "@type": "FAQPage",
    mainEntity: FAQ_ITEMS.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  return (
    <Layout>
      <GlobalSchema pageType="static_page" entityId="preguntas-frecuentes" />
      {isIndexable && (
        <JsonLd
          data={[
            breadcrumbSchema([
              { name: "Inicio", path: "/" },
              { name: "Preguntas frecuentes", path: "/preguntas-frecuentes/" },
            ]),
            faqSchema,
          ]}
        />
      )}

      <div className="container max-w-3xl py-12 md:py-16">
        <h1 className="font-display text-3xl font-bold tracking-tight md:text-4xl">
          Preguntas frecuentes sobre Bandflow
        </h1>

        <p className="mt-6 text-muted-foreground leading-relaxed">
          Bandflow es una plataforma pensada para ayudar a bandas, artistas y promotores a encontrar salas de conciertos con más información, más contexto y más transparencia. Aquí resolvemos las dudas más habituales sobre cómo funciona la plataforma, cómo se gestiona la información y qué papel tienen tanto la comunidad musical como las propias salas.
        </p>

        <div className="mt-10 space-y-8">
          {FAQ_ITEMS.map((item, i) => (
            <section key={i}>
              <h2 className="font-display text-xl font-semibold">{item.question}</h2>
              <p className="mt-2 text-muted-foreground leading-relaxed">{item.answer}</p>
            </section>
          ))}
        </div>

        <section className="mt-14 rounded-lg border bg-secondary/50 p-6 md:p-8">
          <h2 className="font-display text-xl font-semibold">¿Tienes alguna duda?</h2>
          <p className="mt-2 text-muted-foreground leading-relaxed">
            ¿Tienes una duda que no aparece aquí?{" "}
            <Link to="/contacto/" className="font-medium text-primary underline underline-offset-4 hover:text-primary/80">
              Ponte en contacto con Bandflow
            </Link>
            . Queremos construir esta plataforma junto a la comunidad musical.
          </p>
        </section>
      </div>
    </Layout>
  );
}
